import analyticsStorage from './storage';
import { encodeBase64Unicode } from './helpers';
import { sanitizeProperties } from './blocklist';

const MIXPANEL_API_URL = 'https://api.mixpanel.com/track?ip=1&verbose=1';
const BATCH_SIZE = 50;
const SYNC_INTERVAL = 30000;
const MAX_RETRIES = 5;

class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.syncTimer = null;
    this.mixpanelToken = null;
    this.isInitialized = false;
  }

  init(token) {
    if (this.isInitialized && this.mixpanelToken === token) return;

    this.mixpanelToken = token;

    if (!this.isInitialized) {
      window.addEventListener('online', () => this.onOnline());
      window.addEventListener('offline', () => this.onOffline());
      this.isInitialized = true;
    }

    if (navigator.onLine) {
      this.startPeriodicSync();
    }
  }

  onOnline() {
    this.startPeriodicSync();
    this.syncNow();
  }

  onOffline() {
    this.stopPeriodicSync();
  }

  startPeriodicSync() {
    if (this.syncTimer) return;

    this.syncTimer = setInterval(() => {
      this.syncNow();
    }, SYNC_INTERVAL);
  }

  stopPeriodicSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  async syncNow() {
    if (this.isSyncing) {
      return;
    }

    if (!navigator.onLine) {
      return;
    }

    if (!this.mixpanelToken) {
      return;
    }

    this.isSyncing = true;
    try {
      await this.processQueue();
    } finally {
      this.isSyncing = false;
    }
  }

  async processQueue() {
    const stats = await analyticsStorage.getStats();
    
    if (stats.pending === 0) {
      return;
    }

    while (true) {
      const batch = await analyticsStorage.getBatchToSync(BATCH_SIZE);
      
      if (batch.length === 0) {
        break;
      }

      const success = await this.syncBatch(batch);
      
      if (!success) {
        break;
      }
    }
  }

  async syncBatch(events) {
    const eventIds = events.map(e => e.id);

    await analyticsStorage.markAsSyncing(eventIds);

    const mixpanelEvents = events.map(e => ({
      event: e.event,
      properties: {
        ...sanitizeProperties(e.properties),
        distinct_id: e.userId,
        time: Math.floor(e.timestamp / 1000),
        token: this.mixpanelToken,
        $insert_id: e.uuid,

        visit_id: e.visitId,
        device_id: e.deviceId,
        session_id: e.sessionId,

        $os: this.getOS(),
        $browser: this.getBrowser(),
        $current_url: e.properties.page_url || window.location.href,
        $screen_width: window.screen.width,
        $screen_height: window.screen.height,
        mp_lib: 'web',
        $lib_version: '2.47.0',
      },
    }));

    try {
      const response = await this.sendToMixpanel(mixpanelEvents);

      if (response.ok && await this.wasAccepted(response)) {
        await analyticsStorage.markAsSynced(eventIds);
        return true;
      }

      await analyticsStorage.markAsFailed(eventIds);
      return false;
    } catch (error) {
      await analyticsStorage.markAsFailed(eventIds);
      return false;
    }
  }

  async wasAccepted(response) {
    try {
      const result = await response.json();
      return result.status === 1;
    } catch (error) {
      return false;
    }
  }

  async sendToMixpanel(events) {
    const data = encodeBase64Unicode(JSON.stringify(events));

    return fetch(MIXPANEL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: `data=${encodeURIComponent(data)}`
    });
  }

  async getStats() {
    return analyticsStorage.getStats();
  }

  getOS() {
    const ua = navigator.userAgent;
    if (ua.indexOf('Win') !== -1) return 'Windows';
    if (ua.indexOf('Mac') !== -1) return 'Mac OS';
    if (ua.indexOf('Linux') !== -1) return 'Linux';
    if (ua.indexOf('Android') !== -1) return 'Android';
    if (ua.indexOf('iOS') !== -1 || ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1) return 'iOS';
    return 'Unknown';
  }

  getBrowser() {
    const ua = navigator.userAgent;
    if (ua.indexOf('Chrome') !== -1 && ua.indexOf('Edg') === -1) return 'Chrome';
    if (ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1) return 'Safari';
    if (ua.indexOf('Firefox') !== -1) return 'Firefox';
    if (ua.indexOf('Edg') !== -1) return 'Edge';
    if (ua.indexOf('MSIE') !== -1 || ua.indexOf('Trident') !== -1) return 'Internet Explorer';
    return 'Unknown';
  }
}

const syncManager = new SyncManager();
export default syncManager;
